<template>
  <div>
  <v-dialog :value="show" persistent max-width="900" scrollable @input="$emit('close')">
    <v-card class="ec-card">
      <admin-table-subheader
        :title="`${reseller.name} — Pools & Report`"
        :refresh-button="true"
        :refresh-loading="loading"
        close-button
        @refresh="loadAll"
        @close="$emit('close')"
      />

      <v-card-text class="rd-body">
        <div v-if="loading" class="ec-loading">
          <v-progress-circular indeterminate color="primary" size="32" />
        </div>

        <template v-else>
          <!-- ── Totals ─────────────────────────────────────────────── -->
          <div class="rd-stats">
            <div class="rd-stat"><span class="rd-stat__v">{{ totals.allocated }}</span><span class="rd-stat__l">Allocated</span></div>
            <div class="rd-stat"><span class="rd-stat__v">{{ totals.consumed }}</span><span class="rd-stat__l">Consumed (sold)</span></div>
            <div class="rd-stat"><span class="rd-stat__v">{{ totals.remaining }}</span><span class="rd-stat__l">Remaining</span></div>
            <div class="rd-stat"><span class="rd-stat__v">{{ totals.keys_issued }}</span><span class="rd-stat__l">Keys issued</span></div>
            <div class="rd-stat"><span class="rd-stat__v">{{ totals.unbilled_keys }}</span><span class="rd-stat__l">Unbilled</span></div>
          </div>

          <!-- ── Allocate new pool ──────────────────────────────────── -->
          <div class="rd-section">
            <div class="rd-section__title">Allocate New Pool</div>
            <div class="rd-alloc">
              <v-select
                v-model="alloc.licence_type_id"
                :items="licences"
                item-text="licence_name"
                item-value="id"
                label="Licence type" dense outlined hide-details
                style="max-width:220px"
              />
              <v-text-field v-model.number="alloc.allocated" type="number" min="1" label="Quantity" dense outlined hide-details style="max-width:110px" />
              <v-text-field v-model.number="alloc.duration_days" type="number" min="1" label="Duration (days)" dense outlined hide-details style="max-width:130px" />
              <v-text-field v-model="alloc.device_type_restriction" label="Device restriction (opt.)" dense outlined hide-details style="max-width:160px" />
              <button class="ec-btn ec-btn--primary" :disabled="allocating || !alloc.licence_type_id || !alloc.allocated" @click="allocate">
                <v-progress-circular v-if="allocating" size="13" width="2" indeterminate style="margin-right:6px" />
                Allocate
              </button>
            </div>
          </div>

          <!-- ── Pools table ────────────────────────────────────────── -->
          <div class="rd-section">
            <div class="rd-section__title">Pools</div>
            <v-data-table :headers="poolHeaders" :items="pools" :items-per-page="10" class="ec-table" dense>
              <template v-slot:[`item.licence_type_id`]="{ item }">
                {{ licenceMap[item.licence_type_id] || ('#' + item.licence_type_id) }}
              </template>
              <template v-slot:[`item.remaining`]="{ item }">
                <span class="u-status-pill" :class="item.remaining > 0 ? 'u-pill--green' : 'u-pill--red'">
                  {{ item.remaining }} / {{ item.allocated }}
                </span>
              </template>
              <template v-slot:[`item.is_active`]="{ item }">
                <span class="u-status-pill" :class="item.is_active ? 'u-pill--green' : 'u-pill--gray'">
                  {{ item.is_active ? 'Active' : 'Inactive' }}
                </span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <button class="ec-icon-btn" title="Edit quantity" @click="openAllocEdit(item)">
                  <v-icon size="15">mdi-pencil-outline</v-icon>
                </button>
                <button class="ec-icon-btn" :title="item.is_active ? 'Deactivate' : 'Activate'" @click="toggleActive(item)">
                  <v-icon size="15">{{ item.is_active ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                </button>
              </template>
            </v-data-table>
          </div>

          <!-- ── Partner users ──────────────────────────────────────── -->
          <div class="rd-section">
            <div class="rd-section__title">Partner Users</div>
            <div class="rd-alloc">
              <v-text-field v-model="userEmail" label="User email" dense outlined hide-details style="max-width:280px" placeholder="operator@avermedia.com" @keyup.enter="addUser" />
              <button class="ec-btn ec-btn--primary" :disabled="addingUser || !userEmail" @click="addUser">
                <v-progress-circular v-if="addingUser" size="13" width="2" indeterminate style="margin-right:6px" />
                Add
              </button>
            </div>
            <p class="rd-hint">The user must already be registered on the platform; they are granted partner access by email (gains portal access).</p>

            <v-data-table :headers="userHeaders" :items="users" :items-per-page="10" class="ec-table" style="margin-top:10px" dense hide-default-footer>
              <template v-slot:[`item.status`]="{ item }">
                <span class="u-status-pill" :class="item.status === 'active' ? 'u-pill--green' : 'u-pill--gray'">{{ item.status }}</span>
              </template>
              <template v-slot:[`item.created_at`]="{ item }">
                <span class="u-date">{{ fmtDate(item.created_at) }}</span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <button class="ec-icon-btn ec-icon-btn--danger" title="Remove" @click="askRemoveUser(item)">
                  <v-icon size="15">mdi-account-remove-outline</v-icon>
                </button>
              </template>
            </v-data-table>
          </div>
        </template>
      </v-card-text>
    </v-card>
    </v-dialog>

    <!-- ── Edit allocated dialog (replaces Swal input) ──────────────── -->
    <v-dialog v-model="allocEdit.show" max-width="360" persistent>
      <div class="ec-card rd-edit">
        <div class="rd-edit__title">Edit Allocated Quantity</div>
        <p class="rd-hint" style="margin:0 0 10px">Cannot go below the consumed amount ({{ allocEdit.consumed }}).</p>
        <v-text-field v-model.number="allocEdit.value" type="number" :min="allocEdit.consumed" label="New quantity" dense outlined hide-details />
        <div class="rd-edit__footer">
          <button class="ec-btn ec-btn--ghost" @click="allocEdit.show = false">Cancel</button>
          <button class="ec-btn ec-btn--primary" :disabled="allocEdit.saving" @click="saveAllocEdit">
            <v-progress-circular v-if="allocEdit.saving" size="13" width="2" indeterminate style="margin-right:6px" />
            Save
          </button>
        </div>
      </div>
    </v-dialog>

    <confirm-dialog
      :show.sync="confirm.show"
      :title="confirm.title"
      :message="confirm.message"
      :item-name="confirm.itemName"
      :loading="confirm.loading"
      confirm-text="Remove"
      cancel-text="Cancel"
      type="danger"
      @confirm="doRemoveUser"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { apiService } from '../../service/api.service'
import AdminTableSubheader from '../shared/AdminTableSubheader.vue'
import ConfirmDialog from '../shared/ConfirmDialog.vue'

export default {
  name: 'ResellerDetail',
  components: { AdminTableSubheader, ConfirmDialog },
  props: {
    show: { type: Boolean, default: false },
    reseller: { type: Object, required: true }
  },
  data () {
    return {
      loading: false,
      allocating: false,
      addingUser: false,
      pools: [],
      users: [],
      totals: { allocated: 0, consumed: 0, remaining: 0, keys_issued: 0, unbilled_keys: 0 },
      licences: [],
      userEmail: '',
      alloc: { licence_type_id: null, allocated: 100, duration_days: 365, device_type_restriction: '' },
      allocEdit: { show: false, pool: null, value: 0, consumed: 0, saving: false },
      confirm: { show: false, title: '', message: '', itemName: '', loading: false, target: null },
      snackbar: { show: false, text: '', color: 'success' },
      poolHeaders: [
        { text: 'Licence Type', value: 'licence_type_id', align: 'start' },
        { text: 'Remaining / Allocated', value: 'remaining', align: 'center' },
        { text: 'Consumed', value: 'consumed', align: 'center' },
        { text: 'Duration (days)', value: 'duration_days', align: 'center' },
        { text: 'Status', value: 'is_active', align: 'center' },
        { text: 'Action', value: 'actions', align: 'center', sortable: false }
      ],
      userHeaders: [
        { text: 'Email', value: 'email', align: 'start' },
        { text: 'Role', value: 'role', align: 'center' },
        { text: 'Status', value: 'status', align: 'center' },
        { text: 'Added', value: 'created_at', align: 'center' },
        { text: 'Action', value: 'actions', align: 'center', sortable: false }
      ]
    }
  },
  computed: {
    licenceMap () {
      const m = {}
      this.licences.forEach((l) => { m[l.id] = l.licence_name })
      return m
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    notify (text, color = 'success') {
      this.snackbar = { show: true, text, color }
    },
    fmtDate (v) {
      if (!v) return '—'
      const d = new Date(v)
      return isNaN(d) ? v : d.toLocaleDateString()
    },
    async loadAll () {
      this.loading = true
      await Promise.all([this.loadReport(), this.loadLicences(), this.loadUsers()])
      this.loading = false
    },
    async loadReport () {
      await apiService.get(`admin/resellers/${this.reseller.id}/report`)
        .then((res) => { this.pools = res.data.pools || []; this.totals = res.data.totals || this.totals })
        .catch(() => this.notify('Failed to load report', 'error'))
    },
    async loadLicences () {
      await apiService.get('admin-get-licences')
        .then((res) => { this.licences = res.data.data || [] })
        .catch(() => { this.licences = [] })
    },
    async loadUsers () {
      await apiService.get(`admin/resellers/${this.reseller.id}/users`)
        .then((res) => { this.users = res.data || [] })
        .catch(() => this.notify('Failed to load users', 'error'))
    },
    async allocate () {
      this.allocating = true
      const payload = {
        licence_type_id: this.alloc.licence_type_id,
        allocated: this.alloc.allocated,
        duration_days: this.alloc.duration_days,
        device_type_restriction: this.alloc.device_type_restriction || null
      }
      await apiService.post(`admin/resellers/${this.reseller.id}/pools`, payload)
        .then(() => { this.loadReport(); this.alloc.device_type_restriction = ''; this.notify('Pool allocated') })
        .catch((err) => this.notify(err.response?.data?.message || 'Allocation failed', 'error'))
        .finally(() => (this.allocating = false))
    },
    openAllocEdit (pool) {
      this.allocEdit = { show: true, pool, value: pool.allocated, consumed: pool.consumed, saving: false }
    },
    async saveAllocEdit () {
      this.allocEdit.saving = true
      await apiService.put(`admin/resellers/${this.reseller.id}/pools/${this.allocEdit.pool.id}`, { allocated: Number(this.allocEdit.value) })
        .then(() => { this.loadReport(); this.allocEdit.show = false; this.notify('Pool updated') })
        .catch((err) => this.notify(err.response?.data?.message || 'Operation failed', 'error'))
        .finally(() => (this.allocEdit.saving = false))
    },
    async toggleActive (pool) {
      await apiService.put(`admin/resellers/${this.reseller.id}/pools/${pool.id}`, { is_active: !pool.is_active })
        .then(() => { this.loadReport(); this.notify(pool.is_active ? 'Pool deactivated' : 'Pool activated') })
        .catch((err) => this.notify(err.response?.data?.message || 'Operation failed', 'error'))
    },
    async addUser () {
      this.addingUser = true
      await apiService.post(`admin/resellers/${this.reseller.id}/users`, { email: this.userEmail })
        .then(() => { this.userEmail = ''; this.loadUsers(); this.notify('Partner user added') })
        .catch((err) => this.notify(err.response?.data?.message || 'Failed to add user', 'error'))
        .finally(() => (this.addingUser = false))
    },
    askRemoveUser (item) {
      this.confirm = {
        show: true,
        title: 'Remove user',
        message: 'Partner access will be removed:',
        itemName: item.email,
        loading: false,
        target: item
      }
    },
    async doRemoveUser () {
      this.confirm.loading = true
      await apiService.delete(`admin/resellers/${this.reseller.id}/users/${this.confirm.target.id}`)
        .then(() => { this.loadUsers(); this.notify('User removed') })
        .catch((err) => this.notify(err.response?.data?.message || 'Operation failed', 'error'))
        .finally(() => { this.confirm.show = false; this.confirm.loading = false })
    }
  }
}
</script>

<style scoped>
.rd-body { padding: 0 20px 20px; }
.rd-stats { display:flex; gap:12px; flex-wrap:wrap; padding:16px 0; }
.rd-stat { flex:1; min-width:120px; background:var(--ec-gray-bg); border:1px solid var(--ec-border); border-radius:10px; padding:12px 14px; display:flex; flex-direction:column; gap:2px; }
.rd-stat__v { font-size:1.4rem; font-weight:700; color:var(--ec-text-1); }
.rd-stat__l { font-size:.72rem; color:var(--ec-text-2); }
.rd-section { margin-top:16px; }
.rd-section__title { font-size:.82rem; font-weight:600; color:var(--ec-text-1); margin-bottom:8px; }
.rd-alloc { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
.rd-hint { font-size:.72rem; color:var(--ec-text-2); margin-top:6px; }
.rd-edit { background: var(--ec-surface, #fff); border-radius: 12px; padding:18px 20px; }
.rd-edit__title { font-size:.95rem; font-weight:700; color:var(--ec-text-1); margin-bottom:6px; }
.rd-edit__footer { display:flex; justify-content:flex-end; gap:8px; margin-top:16px; }
</style>
